import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Typography } from '@material-ui/core';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isPasswordRecoveryEmailNotFoundError } from '../../util/errors';
import {
  AuthenticationContentWrapper,
  FormattedMessage,
  IconKeys,
  InlineTextButton,
  ModalParagraph,
  ModalTitle,
  TypographyWrapper,
} from '../../components';
import { PasswordRecoveryForm } from '../../forms';
import {
  clearPasswordRecoveryError,
  recoverPassword,
  retypePasswordRecoveryEmail,
} from './PasswordRecoveryPage.duck';

import css from './PasswordRecoveryPage.module.css';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';

export const PasswordRecoveryPageComponent = (props) => {
  const {
    initialEmail,
    submittedEmail,
    recoveryError,
    recoveryInProgress,
    passwordRequested,
    onChange,
    onSubmitEmail,
    onRetypeEmail,
    intl,
  } = props;

  const title = intl.formatMessage({
    id: 'PasswordRecoveryPage.title',
  });

  const resendEmailLink = (
    <InlineTextButton onClick={() => onSubmitEmail(submittedEmail)}>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        <FormattedMessage id="PasswordRecoveryPage.resendEmailLinkText" />
      </TypographyWrapper>
    </InlineTextButton>
  );

  const fixEmailLink = (
    <InlineTextButton onClick={onRetypeEmail}>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        <FormattedMessage id="PasswordRecoveryPage.fixEmailLinkText" />
      </TypographyWrapper>
    </InlineTextButton>
  );

  const submitEmailContent = (
    <>
      <IconKeys className={css.modalIcon} />
      <ModalTitle>
        <FormattedMessage id="PasswordRecoveryPage.forgotPasswordTitle" />
      </ModalTitle>
      <ModalParagraph className={css.modalMessage}>
        <FormattedMessage id="PasswordRecoveryPage.forgotPasswordMessage" />
      </ModalParagraph>
      <PasswordRecoveryForm
        inProgress={recoveryInProgress}
        onChange={onChange}
        onSubmit={(values) => onSubmitEmail(values.email)}
        initialValues={{ email: initialEmail }}
        recoveryError={recoveryError}
      />
    </>
  );

  const submittedEmailText = passwordRequested ? (
    <span className={css.email}>{initialEmail}</span>
  ) : (
    <span className={css.email}>{submittedEmail}</span>
  );

  const emailSubmittedContent = (
    <>
      <IconKeys className={css.modalIcon} />
      <ModalTitle>
        <FormattedMessage id="PasswordRecoveryPage.emailSubmittedTitle" />
      </ModalTitle>
      <ModalParagraph className={css.modalMessage}>
        <FormattedMessage
          id="PasswordRecoveryPage.emailSubmittedMessage"
          values={{ submittedEmailText }}
        />
      </ModalParagraph>
      <div className={css.bottomWrapper}>
        <p className={css.helperText}>
          <Typography variant="body1">
            {recoveryInProgress ? (
              <FormattedMessage id="PasswordRecoveryPage.resendingEmailInfo" />
            ) : (
              <FormattedMessage
                id="PasswordRecoveryPage.resendEmailInfo"
                values={{ resendEmailLink }}
              />
            )}
          </Typography>
        </p>
        <p className={css.helperText}>
          <Typography variant="body1">
            <FormattedMessage id="PasswordRecoveryPage.fixEmailInfo" values={{ fixEmailLink }} />
          </Typography>
        </p>
      </div>
    </>
  );

  const genericErrorContent = (
    <>
      <IconKeys className={css.modalIcon} />
      <ModalTitle>
        <FormattedMessage id="PasswordRecoveryPage.actionFailedTitle" />
      </ModalTitle>
      <ModalParagraph className={css.modalMessage}>
        <FormattedMessage id="PasswordRecoveryPage.actionFailedMessage" />
      </ModalParagraph>
    </>
  );

  let content;
  if (isPasswordRecoveryEmailNotFoundError(recoveryError)) {
    content = submitEmailContent;
  } else if (recoveryError) {
    content = genericErrorContent;
  } else if (submittedEmail || passwordRequested) {
    content = emailSubmittedContent;
  } else {
    content = submitEmailContent;
  }

  return <AuthenticationContentWrapper title={title}>{content}</AuthenticationContentWrapper>;
};

PasswordRecoveryPageComponent.defaultProps = {
  initialEmail: null,
  submittedEmail: null,
  recoveryError: null,
};

const { bool, func, string } = PropTypes;

PasswordRecoveryPageComponent.propTypes = {
  initialEmail: string,
  submittedEmail: string,
  recoveryError: propTypes.error,
  recoveryInProgress: bool.isRequired,
  passwordRequested: bool.isRequired,
  onChange: func.isRequired,
  onSubmitEmail: func.isRequired,
  onRetypeEmail: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
  const { initialEmail, submittedEmail, recoveryError, recoveryInProgress, passwordRequested } =
    state.PasswordRecoveryPage;
  return {
    initialEmail,
    submittedEmail,
    recoveryError,
    recoveryInProgress,
    passwordRequested,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onChange: () => dispatch(clearPasswordRecoveryError()),
  onSubmitEmail: (email) => dispatch(recoverPassword(email)),
  onRetypeEmail: () => dispatch(retypePasswordRecoveryEmail()),
});

const PasswordRecoveryPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(PasswordRecoveryPageComponent);

export default PasswordRecoveryPage;
